import {Link} from "react-router";
import {BarChart3, Building2, CreditCard, KeyRound, Plus, User} from "lucide-react";
import {Badge} from "@nosible/shared/components/ui/badge";
import {Button} from "@nosible/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {useSession} from "@/account/lib/auth-client";
import {trpc} from "@/account/lib/trpc";
import {AccountPageHeader} from "@/account/components/AccountPageHeader";
import {CancelSubscription} from "@/account/components/CancelSubscription";

export function SubscriptionsPage() {
  const {data: session} = useSession();
  const profile = trpc.user.getProfile.useQuery(undefined, {
    enabled: Boolean(session),
  });
  const subscriptions = (profile.data?.subscriptions ?? []).filter(
    (subscription) => {
      const status = subscription.status.toLowerCase();
      return status !== "canceled" && status !== "cancelled";
    },
  );

  return (
    <div className="account-ui-page account-settings">
      <AccountPageHeader
        title="Your subscriptions"
        description="API keys and usage belong to subscriptions. Open one to manage billing, keys, and usage."
        icon={KeyRound}
      />

      <div className="mb-6 flex justify-end">
        <Button variant="outline" asChild>
          <Link to="/new-subscription">
            <Plus aria-hidden="true" /> ADD A NEW PRODUCT
          </Link>
        </Button>
      </div>

      {profile.isLoading ? (
        <p className="text-chromium-mist">Loading subscriptions…</p>
      ) : subscriptions.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No active subscriptions</CardTitle>
            <CardDescription>
              Add a NOSIBLE product to begin managing API access.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="space-y-5">
          {subscriptions.map((subscription) => (
            <SubscriptionSummaryCard key={subscription.id} subscription={subscription} />
          ))}
        </div>
      )}
    </div>
  );
}

function SubscriptionSummaryCard({subscription}: {subscription: any}) {
  const {data: apiKeys, isLoading: keysLoading} = trpc.subscription.apiKey.listApiKeys.useQuery({
    subscriptionId: subscription.id,
  });
  const {data: currentUsage, isLoading: usageLoading} = trpc.subscription.usage.currentUsage.useQuery({
    subscriptionId: subscription.id,
  });

  const used = currentUsage?.usage?.splits?.reduce((total: number, entry: {used: number}) => total + entry.used, 0) ?? 0;
  const limit = currentUsage?.usage?.splits?.reduce((total: number, entry: {limit: number}) => total + entry.limit, 0) ?? 0;
  const usageLabel = usageLoading ? "Loading" : limit > 0 ? `${used.toLocaleString()} / ${limit.toLocaleString()}` : "No usage yet";
  const OwnerIcon = subscription.ownerType === "organization" ? Building2 : User;
  const isManageable = subscription.status === "active" || subscription.status === "trialing";

  return (
    <Card className="flex h-full flex-col">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CreditCard className="brand-card-icon" aria-hidden="true" />
            <CardTitle>{subscription.name || subscription.plan.name}</CardTitle>
            <CardDescription className="mt-2 flex items-center gap-2">
              <OwnerIcon className="h-4 w-4" aria-hidden="true" />
              {subscription.ownerType === "organization" ? `${subscription.ownerName} organization` : "Personal subscription"}
            </CardDescription>
          </div>
          <Badge variant={subscription.status.toLowerCase() === "active" ? "default" : "secondary"}>
            {subscription.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="mt-auto space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-slate-core bg-slate-core/40 p-3">
            <div className="flex items-center gap-2 text-xs text-chromium-mist"><KeyRound className="h-3.5 w-3.5" />API keys</div>
            <p className="mt-2 text-lg font-semibold text-silica">{keysLoading ? "—" : apiKeys?.length ?? 0}</p>
          </div>
          <div className="rounded-lg border border-slate-core bg-slate-core/40 p-3">
            <div className="flex items-center gap-2 text-xs text-chromium-mist"><BarChart3 className="h-3.5 w-3.5" />Current usage</div>
            <p className="mt-2 text-lg font-semibold text-silica">{usageLabel}</p>
          </div>
        </div>
        <p className="text-sm text-chromium-mist">Open this subscription to manage its billing, API keys, and usage details.</p>
        <div className="flex flex-wrap gap-2">
          <Button asChild className="flex-1" variant="outline">
            <Link to={`/subscriptions/${subscription.id}`}>MANAGE SUBSCRIPTION</Link>
          </Button>
          {isManageable && (
            <>
              <Button asChild variant="outline">
                <Link to={`/subscriptions/${subscription.id}/upgrade`}>CHANGE PLAN</Link>
              </Button>
              <CancelSubscription subscriptionId={subscription.id} onSuccess={() => window.location.reload()} />
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
